/**
 * VB Server - Invitations API
 *
 * CRUD operations for organization invitations.
 *
 * @example
 * import { listInvites, createInvite, deleteInvite } from '@/api/server/invites';
 *
 * const invites = await listInvites(orgId);
 * const newInvite = await createInvite(orgId, { email: 'user@example.com' });
 */

import { serverGet, serverPost, serverDelete } from './client';
import type { OrgRole } from '../../v2/services/auth';

// =============================================================================
// Types
// =============================================================================

/**
 * Invitation entity from vb-server
 */
export interface Invite {
  invite_id: string;
  org_id: string;
  email: string;
  name?: string | null;
  org_role?: OrgRole;
  admin: boolean; // deprecated: use org_role
  send_allowed: boolean; // deprecated: role determines access
  status: number;
  created: string;
  modified: string;
}

/**
 * Input for creating a new invitation
 */
export interface CreateInviteInput {
  email: string;
  name?: string;
  org_role?: OrgRole;
  admin?: boolean; // deprecated: use org_role
  send_allowed?: boolean; // deprecated
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * List all pending invitations for an organization
 *
 * @param orgId - Organization ID
 * @returns Array of invitations
 */
export async function listInvites(orgId: string): Promise<Invite[]> {
  return serverGet<Invite[]>(`/orgs/${orgId}/invites`);
}

/**
 * Get a single invitation by ID
 *
 * @param orgId - Organization ID
 * @param inviteId - Invitation ID
 * @returns Invitation or null if not found
 */
export async function getInvite(orgId: string, inviteId: string): Promise<Invite | null> {
  try {
    const invites = await listInvites(orgId);
    return invites.find((i) => i.invite_id === inviteId) || null;
  } catch {
    return null;
  }
}

/**
 * Create a new invitation
 *
 * @param orgId - Organization ID
 * @param data - Invitation data
 * @returns Created invitation
 */
export async function createInvite(orgId: string, data: CreateInviteInput): Promise<Invite> {
  return serverPost<Invite>(`/orgs/${orgId}/invites`, data);
}

/**
 * Delete/cancel an invitation (hard delete)
 *
 * @param orgId - Organization ID
 * @param inviteId - Invitation ID
 */
export async function deleteInvite(orgId: string, inviteId: string): Promise<void> {
  await serverDelete(`/orgs/${orgId}/invites/${inviteId}`);
}

/**
 * Resend an invitation email
 * Resends the invitation email and increments send_count
 *
 * @param orgId - Organization ID
 * @param inviteId - Invitation ID to resend
 * @returns Updated invitation
 */
export async function resendInvite(orgId: string, inviteId: string): Promise<Invite> {
  return serverPost<Invite>(`/orgs/${orgId}/invites/${inviteId}/resend`, {});
}
